import { useI18n } from '@vista-digital/occ-ui'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'
import { RouterProvider, createBrowserRouter, createMemoryRouter, useLocation, useNavigate } from 'react-router-dom'
import { navigationService } from '../content/services/navigation.service'
import { urlService } from '../content/services/url.service'
import { getRoutes } from './getRoutes'
import { Layout } from './Layout'
import { PagesEnum } from '../content/pages/pages'

export interface PagesRouterOverrideProps extends LayoutWrapperOverrideProps {
    overridePageComponent?: {
        pageKey: PagesEnum
        component: FunctionComponent
    }
    overridePath?: string
    overrideSearchParams?: { [key: string]: string }
}

export const PagesRouter: FunctionComponent<PagesRouterOverrideProps> = ({
    overridePageComponent,
    overridePath,
    overrideSearchParams,
    ...layoutOverrideProps
}) => {
    urlService.setI18n(useI18n())

    const allRoutes = useMemo(() => getRoutes(overridePageComponent), [])

    if (allRoutes.length === 0) return null

    navigationService.setRoutes(allRoutes)
    const routerArgs = [{ Component: () => <LayoutWrapper {...layoutOverrideProps} />, children: allRoutes }]

    if (overridePath) {
        // eslint-disable-next-line no-var -- allows for easier creation of two different types of routers
        var r = createMemoryRouter(routerArgs, {
            initialEntries: [
                {
                    pathname: overridePath,
                    search: new URLSearchParams(overrideSearchParams ?? '').toString(),
                },
            ],
        })
    } else {
        // eslint-disable-next-line no-var -- allows for easier creation of two different types of routers
        var r = createBrowserRouter(routerArgs)
    }

    return <RouterProvider router={r} />
}

interface LayoutWrapperOverrideProps {
    layoutOverride?: FunctionComponent
}

export const LayoutWrapper: FunctionComponent<LayoutWrapperOverrideProps> = props => {
    const navigate = useNavigate()
    const location = useLocation()

    const Element = useMemo(() => props?.layoutOverride ?? (() => <Layout />), [props?.layoutOverride])

    // Trailing slash not always added by React Router, but we always want to have one, especially for Cinemas, Popups and HelloBar
    if (!location.pathname.endsWith('/')) {
        location.pathname += '/'
        navigate(location, { replace: true })
        return null
    }

    navigationService.setNavigate(navigate)
    navigationService.setLocation(location)

    return <Element />
}
